import { PageHeader } from "@/components/layout/page-header";
import { SetForm } from "@/components/reconciliation/set-form";
import { useReconciliationToasts } from "@/components/reconciliation/use-reconciliation-toasts";
import {
	createReconciliationSet,
	getReconciliationSet,
	prepareReconciliationSet,
	updateReconciliationSet,
} from "@/services/api";
import { reconciliationMessages } from "@/services/messages";
import { useTabActions, useTabStore } from "@/stores/tab-store";
import type {
	CreateReconciliationSetRequest,
	ReconciliationSet,
	UpdateReconciliationSetRequest,
} from "@efes/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";

/**
 * Страница создания / редактирования набора сверки (T041, US1).
 *
 * Режим определяется наличием `setId` в активном табе `reconciliation-create`.
 * - Без setId: пустая форма, POST /sets.
 * - С setId: загружает существующий набор, PATCH /sets/:id.
 *
 * «Сохранить как черновик» — простой save, переключение на detail-таб.
 * «Сохранить и подготовить» — save → POST /sets/:id/prepare → toast по результату.
 */
export function ReconciliationCreatePage() {
	const queryClient = useQueryClient();
	const { openReconciliationDetailTab, openReconciliationListTab, closeTab } = useTabActions();
	const toasts = useReconciliationToasts();
	const [issues, setIssues] = useState<string[]>([]);

	// Получаем setId из текущего таба reconciliation-create.
	const setId = useTabStore((state) => {
		const tab = state.tabs.find((t) => t.id === "reconciliation-create");
		return tab?.setId;
	});

	const isEdit = setId !== undefined;

	const { data: existingSet, isLoading: isLoadingSet } = useQuery({
		queryKey: ["reconciliation-set", setId],
		queryFn: () => (setId !== undefined ? getReconciliationSet(setId) : Promise.reject()),
		enabled: setId !== undefined,
	});

	const createMutation = useMutation({
		mutationFn: (input: CreateReconciliationSetRequest) => createReconciliationSet(input),
	});

	const updateMutation = useMutation({
		mutationFn: ({ id, input }: { id: number; input: UpdateReconciliationSetRequest }) =>
			updateReconciliationSet(id, input),
	});

	const prepareMutation = useMutation({
		mutationFn: (id: number) => prepareReconciliationSet(id),
	});

	const isPending =
		createMutation.isPending || updateMutation.isPending || prepareMutation.isPending;

	const invalidate = () => {
		queryClient.invalidateQueries({ queryKey: ["reconciliation-sets"] });
		if (setId !== undefined) {
			queryClient.invalidateQueries({ queryKey: ["reconciliation-set", setId] });
		}
	};

	const saveOrUpdate = async (
		input: CreateReconciliationSetRequest | UpdateReconciliationSetRequest,
	): Promise<ReconciliationSet> => {
		if (isEdit && setId !== undefined) {
			return updateMutation.mutateAsync({ id: setId, input });
		}
		return createMutation.mutateAsync(input as CreateReconciliationSetRequest);
	};

	/**
	 * Сохранение набора (create или update) + фоновый pre-flight для surfaceing
	 * issues пользователю до того, как он кликнет "Запустить сверку".
	 *
	 * Раньше было два handler'а / две кнопки: один просто сохранял, второй
	 * сохранял + вызывал prepare. После упразднения `ready` обе кнопки
	 * сохраняли как `draft` (prepare больше не меняет статус), отличие было
	 * только в том, что вторая показывала issues. Теперь это поведение по
	 * умолчанию: success-toast + pre-flight (проглатываем сетевые ошибки —
	 * валидация не критична для самого факта сохранения).
	 */
	const handleSave = async (
		input: CreateReconciliationSetRequest | UpdateReconciliationSetRequest,
	) => {
		setIssues([]);
		try {
			const set = await saveOrUpdate(input);
			toasts.showSuccess(isEdit ? "updated" : "created");
			invalidate();

			try {
				const result = await prepareMutation.mutateAsync(set.id);
				if (!result.ok) {
					setIssues(result.issues);
					toasts.showValidationError(result.issues);
				}
			} catch {
				// Pre-flight упал — не блокируем UX. Issues всплывут при попытке
				// запустить сверку из детальной карточки (там свой inline pre-flight).
			}

			openReconciliationDetailTab(set.id, set.name);
		} catch (err) {
			toasts.showApiError(err);
		}
	};

	/**
	 * «Отмена»: пользователь закрывает форму без сохранения.
	 *  - edit-режим (existingSet присутствует): возвращаем на detail-таб
	 *    редактируемого набора, форму закрываем.
	 *  - create-режим: возвращаем на список наборов, форму закрываем.
	 */
	const handleCancel = () => {
		if (isEdit && existingSet) {
			openReconciliationDetailTab(existingSet.id, existingSet.name);
		} else {
			openReconciliationListTab();
		}
		closeTab("reconciliation-create");
	};

	if (isEdit && isLoadingSet) {
		return (
			<div className="flex items-center justify-center py-16 text-sm text-slate-500">
				<Loader2 className="mr-2 h-4 w-4 animate-spin" /> {reconciliationMessages.loading}
			</div>
		);
	}

	return (
		<div className="mx-auto max-w-5xl space-y-6">
			<PageHeader
				title={
					isEdit ? reconciliationMessages.page_edit_title : reconciliationMessages.page_create_title
				}
			/>

			{issues.length > 0 && (
				<div className="rounded-lg border border-red-200 bg-red-50 p-4">
					<h3 className="text-sm font-semibold text-red-800">
						{reconciliationMessages.toast_validation_error}
					</h3>
					<ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-red-700">
						{issues.map((issue) => (
							<li key={issue}>{issue}</li>
						))}
					</ul>
				</div>
			)}

			<SetForm
				initialValue={existingSet}
				onSave={handleSave}
				onCancel={handleCancel}
				isPending={isPending}
			/>
		</div>
	);
}
